import type {CmLogger} from "@cm/log"
import {Injector} from "@angular/core"
import {
    ConfiguratorCreateJobForArHelperGQL,
    ConfiguratorCreateDataObjectAssignmentForArHelperGQL,
    DeleteDataObjectAssignmentForArHelperGQL,
    ConfiguratorAllGltfAssignmentsGQL,
    ConfiguratorGltfDataObjectAssignmentsGQL,
    ConfiguratorJobTaskForArHelperGQL,
    DeleteDataObjectForArHelperGQL,
} from "@app/common/helpers/ar/ar.helper.generated"
import {Settings} from "@app/common/models/settings/settings"
import {arGltfGenerationTask, arUsdzGenerationTask} from "@cm/job-nodes/ar"
import {JobNodes} from "@cm/job-nodes/job-nodes"
import {
    ConfigInfo,
    FindMaterial,
    MaterialInfo,
    MaterialReference,
    Parameters,
    ParametersParameters,
    TemplateGraph,
    AnyJsonValue,
    isTemplateNode,
} from "@cm/template-nodes"
import {graphToJson, sortedJSONStringify} from "@cm/utils"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {CM_LOGGER_AR} from "@common/services/logging/index"
import {filter, firstValueFrom, map, take} from "rxjs"
import {ContentTypeModel, DataObjectAssignmentType, JobState} from "@generated"
import {fetchThrowingErrors} from "@app/common/helpers/api/fetch"

export type ArContentModel = "model/vnd.usdz+zip" | "model/gltf-binary"

export const getParametersForArGeneration = (sceneManagerService: SceneManagerService): Parameters => {
    const configuration: ParametersParameters = {}

    for (const descriptor of sceneManagerService.getDescriptors()) {
        if (descriptor.props.type !== "input") continue

        const {id} = descriptor.props
        if (descriptor.props.value === null) continue

        if (descriptor instanceof ConfigInfo) configuration[id] = descriptor.props.value.id
        else if (descriptor instanceof MaterialInfo) {
            if (descriptor.props.origin instanceof MaterialReference || descriptor.props.origin instanceof FindMaterial)
                configuration[id] = descriptor.props.origin
        }
    }

    return new Parameters({parameters: configuration})
}

export const getArAssignmentKey = (parameters: Parameters) => {
    const transformed: {[paramId: string]: AnyJsonValue} = {}
    for (const key of Object.keys(parameters.parameters)) {
        const value = parameters.parameters[key]
        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") transformed[key] = value
        else if (isTemplateNode(value)) {
            if (value instanceof MaterialReference)
                transformed[key] = `MaterialReference: ${value.parameters.materialId}-${value.parameters.materialRevisionId}`
            else if (value instanceof FindMaterial) transformed[key] = `FindMaterial: ${value.parameters.articleId}`
            else throw new Error(`Unsupported template node for AR assignment key: ${value.constructor.name} for key ${key}`)
        } else throw new Error(`Unsupported parameter type for AR assignment key: ${typeof value} for key ${key}`)
    }

    return sortedJSONStringify(transformed)
}

const makeJobGraph = (templateRevisionId: string, configurationBase64: string, assignmentKey: string) => {
    const gltfGenerationTask = JobNodes.task(arGltfGenerationTask, {
        input: JobNodes.struct({
            templateRevisionId: JobNodes.value(templateRevisionId),
            templateParametersBase64: JobNodes.value(configurationBase64),
            assignmentKey: JobNodes.value(assignmentKey),
        }),
    })
    const usdzGenerationTask = JobNodes.task(arUsdzGenerationTask, {
        input: gltfGenerationTask,
    })

    return JobNodes.jobGraph(JobNodes.list([gltfGenerationTask, usdzGenerationTask]), {
        platformVersion: Settings.APP_VERSION,
    })
}

export const generateNewArModels = async (
    templateId: string,
    templateRevisionId: string,
    currentParams: Parameters,
    organizationLegacyId: number,
    injector: Injector,
) => {
    const templateParametersBase64 = btoa(JSON.stringify(currentParams.serialize()))

    arLogger?.debug(`Starting AR generation job for revision ${templateRevisionId} of template ${templateId}`)
    arLogger?.debug("URL parameters:", `?templateRevisionId=${templateRevisionId}&templateParametersBase64=${templateParametersBase64}`)

    const logger = injector.get(CM_LOGGER_AR)

    return mutateThrowingErrors(injector.get(ConfiguratorCreateJobForArHelperGQL))({
        input: {
            graph: graphToJson(makeJobGraph(templateRevisionId, templateParametersBase64, getArAssignmentKey(currentParams)), logger),
            name: "AR generation",
            organizationLegacyId: organizationLegacyId,
        },
    })
}

export const waitForJobToFinish = async (jobId: string, injector: Injector) => {
    const jobStateWithOutput = injector.get(ConfiguratorJobTaskForArHelperGQL)
    const jobTask = await firstValueFrom(
        jobStateWithOutput.watch({id: jobId}, {pollInterval: 2000}).valueChanges.pipe(
            map(({data: {configuratorJobTask: jobTask}}) => jobTask),
            filter((jobTask) => {
                if (!jobTask) return true
                switch (jobTask.state) {
                    case JobState.Runnable:
                    case JobState.Running:
                    case JobState.Init:
                        return false
                    default:
                        return true
                }
            }),
            take(1),
        ),
    )

    if (!jobTask || jobTask.state !== JobState.Complete) {
        throw new Error("AR generation failed")
    }
}

export const runBatchedArJobs = async (
    arCreationParameters: Parameters[],
    templateId: string,
    templateRevisionId: string,
    organizationLegacyId: number,
    concurrency: number,
    injector: Injector,
) => {
    const createAndWaitForJob = async (params: Parameters) => {
        const {configuratorCreateJob: arGenerationJob} = await generateNewArModels(templateId, templateRevisionId, params, organizationLegacyId, injector)
        try {
            await waitForJobToFinish(arGenerationJob.id, injector)
        } catch {
            const templateParametersBase64 = btoa(JSON.stringify(params.serialize()))
            arLogger?.error("AR generation failed for")
            arLogger?.debug("URL parameters:", `?templateRevisionId=${templateRevisionId}&templateParametersBase64=${templateParametersBase64}`)
        }
    }

    const jobPromises: Promise<void>[] = []
    for (const params of arCreationParameters) {
        jobPromises.push(createAndWaitForJob(params))
        if (jobPromises.length >= concurrency) {
            await Promise.all(jobPromises)
            jobPromises.length = 0
        }
    }

    if (jobPromises.length) await Promise.all(jobPromises)
}

export const computeArLodAssignmentKey = async (
    sceneManagerService: SceneManagerService,
    templateRevisionId: string,
    parameters: Parameters,
    templateGraph: TemplateGraph,
) => {
    sceneManagerService.$lodType.set("ar")
    sceneManagerService.$templateRevisionId.set(templateRevisionId)
    sceneManagerService.$instanceParameters.set(parameters)
    sceneManagerService.$templateGraph.set(templateGraph)
    sceneManagerService.compileTemplate()
    await sceneManagerService.sync(true)
    return getArAssignmentKey(getParametersForArGeneration(sceneManagerService))
}

export const assignGltfDataObject = async (dataObjectId: string, templateRevisionId: string, assignmentKey: string, injector: Injector) => {
    await mutateThrowingErrors(injector.get(ConfiguratorCreateDataObjectAssignmentForArHelperGQL))({
        input: {
            dataObjectId,
            contentTypeModel: ContentTypeModel.TemplateRevision,
            objectId: templateRevisionId,
            type: DataObjectAssignmentType.CachedTemplateGltf,
            assignmentKey,
        },
    })
}

export const getAllGlftAssignments = async (dataObjectId: string, injector: Injector) => {
    const {configuratorDataObjectAssignments} = await fetchThrowingErrors(injector.get(ConfiguratorAllGltfAssignmentsGQL))({
        filter: {dataObjectId, assignmentType: [DataObjectAssignmentType.CachedTemplateGltf]},
    })
    return configuratorDataObjectAssignments
}

export const getGltfDataObjects = async (templateRevisionId: string, assignmentKey: string, injector: Injector) => {
    const {configuratorDataObjectAssignments} = await fetchThrowingErrors(injector.get(ConfiguratorGltfDataObjectAssignmentsGQL))({
        filter: {
            contentTypeModel: ContentTypeModel.TemplateRevision,
            objectId: templateRevisionId,
            assignmentKey: {equals: assignmentKey},
            assignmentType: [DataObjectAssignmentType.CachedTemplateGltf],
        },
    })
    return configuratorDataObjectAssignments
}

export const assignWebKeyToExistingArModel = async (templateRevisionId: string, arAssignmentKey: string, webAssignmentKey: string, injector: Injector) => {
    if (arAssignmentKey === webAssignmentKey) return
    const arKeyAssignments = (await getGltfDataObjects(templateRevisionId, arAssignmentKey, injector)).filter((x) => x !== null)

    if (arKeyAssignments.length === 0) throw new Error("No ar model found")
    await assignGltfDataObject(arKeyAssignments[0].dataObject.id, templateRevisionId, webAssignmentKey, injector)
}

export const assignWebKeysToExistingArModel = async (templateRevisionId: string, webKeyToArKeyMap: Map<string, string>, injector: Injector) => {
    for (const [webKey, arKey] of webKeyToArKeyMap) {
        if (arKey === webKey) continue
        arLogger?.debug(`Assigning web key ${webKey} to ar model of ${arKey}`)
        const dataObjectAssignments = await getGltfDataObjects(templateRevisionId, webKey, injector)
        for (const dataObjectAssignment of dataObjectAssignments)
            if (dataObjectAssignment !== null) await deleteDataObjectAssignment(dataObjectAssignment.id, injector)

        try {
            await assignWebKeyToExistingArModel(templateRevisionId, arKey, webKey, injector)
        } catch (e) {
            arLogger?.error("Error assigning web key to AR model", e)
        }
    }
}

export const deleteDataObjectAssignment = async (dataObjectAssignmentId: string, injector: Injector) => {
    await mutateThrowingErrors(injector.get(DeleteDataObjectAssignmentForArHelperGQL))({id: dataObjectAssignmentId})
}

export const deleteDataObject = async (dataObjectId: string, injector: Injector) => {
    await mutateThrowingErrors(injector.get(DeleteDataObjectForArHelperGQL))({dataObjectId: dataObjectId})
}

export let arLogger: CmLogger | undefined
